import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { PRODUCTS } from "@/data/site";
import { useQuote } from "@/context/QuoteContext";

export default function Products() {
  const { openQuote } = useQuote();

  return (
    <section id="products" data-testid="products-section" className="relative bg-[var(--muted)]/60 py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <nav aria-label="Breadcrumb" data-testid="products-breadcrumb" className="mb-10 flex items-center gap-1.5 text-xs text-[var(--ink-soft)]">
          <a href="#home" className="hover:text-[var(--green)]">Home</a>
          <ChevronRight size={13} />
          <span className="font-semibold text-[var(--green)]">Products</span>
        </nav>

        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <Overline>Our Wholesale Range</Overline>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-[var(--ink)] sm:text-5xl">
              Premium produce & spices, supplied in bulk.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-xs text-sm text-[var(--ink-soft)]">
              Every product is graded for quality and available for wholesale orders with flexible packaging.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <motion.article
              key={p.id}
              data-testid={`product-card-${p.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group flex flex-col overflow-hidden rounded-3xl border border-[var(--border)] bg-white transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-black/10"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.img}
                  alt={p.name}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-2xl font-semibold text-[var(--ink)]">{p.name}</h3>
                <p className="mt-2 flex-1 text-sm text-[var(--ink-soft)]">{p.desc}</p>
                <button
                  onClick={() => openQuote(p.name)}
                  data-testid={`request-price-${p.id}`}
                  className="group/btn mt-6 flex items-center justify-between rounded-full border border-[var(--green)] px-5 py-3 text-sm font-semibold text-[var(--green)] transition-all duration-300 hover:bg-[var(--green)] hover:text-white"
                >
                  Request Price
                  <ArrowUpRight size={17} className="transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
